import type {
  AdminCampaignLinkCreate,
  AdminCampaignLinkList,
  AdminConversionResponse,
  CampaignLink,
  AdminStats,
  AdminUsageResponse,
  AdminUserListResponse,
  AdminUserDetail,
  AdminQuotaState,
  AdminRecipeListResponse,
  AdminJobListResponse,
  AdminQueuesResponse,
  ApiResponse,
  AiJobStatus,
  AdminProxy,
  AdminProxyCreate,
  AdminProxyList,
  AdminProxyTestResult,
  AdminProxyUpdate,
} from '@chefty/shared-types';
import { api } from './api';

/** Unwrap the {success,data} envelope or throw the server's message. */
async function unwrap<T>(res: ApiResponse<T>): Promise<T> {
  if (!res.success || res.data === undefined) {
    throw new Error(res.error?.message ?? 'Request failed');
  }
  return res.data;
}

const get = <T>(endpoint: string): Promise<T> => api.get<T>(endpoint).then(unwrap);

type Query = Record<string, string | number | undefined>;

function qs(params: Query): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Typed wrappers over the `/admin` endpoints. Each returns the shared contract type. */
export const adminApi = {
  stats: () => get<AdminStats>('/admin/stats'),
  usage: (days: number) => get<AdminUsageResponse>(`/admin/usage${qs({ days })}`),
  conversion: (days: number) => get<AdminConversionResponse>(`/admin/conversion${qs({ days })}`),
  users: (p: { page?: number; pageSize?: number; q?: string }) =>
    get<AdminUserListResponse>(`/admin/users${qs(p)}`),
  user: (id: string) => get<AdminUserDetail>(`/admin/users/${id}`),
  /** Set a user's permanent extra weekly imports; returns the recomputed state. */
  setQuota: (id: string, extra: number) =>
    api.patch<AdminQuotaState>(`/admin/users/${id}/quota`, { extra }).then(unwrap),
  /** Forgive the user's current quota window; returns the fresh state. */
  resetQuota: (id: string) =>
    api.post<AdminQuotaState>(`/admin/users/${id}/quota/reset`, {}).then(unwrap),
  recipes: (p: { page?: number; pageSize?: number; q?: string }) =>
    get<AdminRecipeListResponse>(`/admin/recipes${qs(p)}`),
  jobs: (p: { page?: number; pageSize?: number; status?: AiJobStatus }) =>
    get<AdminJobListResponse>(`/admin/jobs${qs(p)}`),
  queues: () => get<AdminQueuesResponse>('/admin/jobs/queue'),
  /** Short campaign links (/go/<slug>) and their click counts. */
  links: () => get<AdminCampaignLinkList>('/admin/links'),
  createLink: (body: AdminCampaignLinkCreate) =>
    api.post<CampaignLink>('/admin/links', body).then(unwrap),
  deleteLink: (id: string) => api.delete<null>(`/admin/links/${id}`).then(unwrap),
  /** Egress proxies for the social media path. Never returns credentials. */
  proxies: () => get<AdminProxyList>('/admin/proxies').then((r) => r.proxies),
  createProxy: (body: AdminProxyCreate) =>
    api.post<AdminProxy>('/admin/proxies', body).then(unwrap),
  updateProxy: (id: string, body: AdminProxyUpdate) =>
    api.patch<AdminProxy>(`/admin/proxies/${id}`, body).then(unwrap),
  deleteProxy: (id: string) => api.delete<null>(`/admin/proxies/${id}`).then(unwrap),
  testProxy: (id: string) =>
    api.post<AdminProxyTestResult>(`/admin/proxies/${id}/test`, {}).then(unwrap),
};
